/**
 * ToastExamples.tsx
 * Comprehensive examples of HeroUI Toast component
 * 
 * Showcases all HeroUI Toast features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { addToast, closeAll } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Select, SelectItem } from '@enfomentis/ui';
import { 
  CheckCircle,
  AlertTriangle,
  Info,
  TrendingUp,
  TrendingDown,
  Activity,
  DollarSign,
  AlertCircle,
  Bell,
  Clock,
  Wifi,
  WifiOff,
  Star,
  Heart,
  Zap,
  Shield
} from 'lucide-react';

/**
 * Main Toast Examples Component
 * Wrapped with ToastProvider to enable toast functionality
 */
const ToastExamplesContent: React.FC = () => {
  const [toastPlacement, setToastPlacement] = useState<'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'>('top-right');

  // Promise simulation for loading states
  const simulatePromise = (shouldResolve: boolean = true, delay: number = 2000) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (shouldResolve) {
          resolve('Operation completed successfully!');
        } else {
          reject(new Error('Operation failed!'));
        }
      }, delay);
    });
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Controls Section */}
        <Card className="bg-surface/50">
          <CardHeader>
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-text-primary">Toast Controls</h3>
              <div className="flex gap-2">
                <Button
                  color="danger" 
                  variant="flat"
                  size="sm"
                  onPress={closeAll}
                >
                  Clear All Toasts
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex gap-4 items-center">
              <label className="text-sm text-text-secondary min-w-[80px]">Placement:</label>
              <Select
                selectedKeys={[toastPlacement]}
                onSelectionChange={(keys) => setToastPlacement(Array.from(keys)[0] as typeof toastPlacement)}
                className="w-48"
                size="sm"
              >
                <SelectItem key="top-left">Top Left</SelectItem>
                <SelectItem key="top-center">Top Center</SelectItem>
                <SelectItem key="top-right">Top Right</SelectItem>
                <SelectItem key="bottom-left">Bottom Left</SelectItem>
                <SelectItem key="bottom-center">Bottom Center</SelectItem>
                <SelectItem key="bottom-right">Bottom Right</SelectItem>
              </Select>
              <Badge color="primary" variant="flat" size="sm">
                Current: {toastPlacement}
              </Badge>
            </div>
          </CardBody>
        </Card>

        {/* Toast Colors */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Toast Colors</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Button
              color="default"
              variant="flat"
              startContent={<Info className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Default Toast',
                description: 'This is a default toast notification.',
                color: 'default',
                icon: <Info className="w-4 h-4" />
              })}
            >
              Default Toast
            </Button>
            
            <Button
              color="primary"
              variant="flat"
              startContent={<Bell className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Primary Toast',
                description: 'Important system announcement.',
                color: 'primary',
                icon: <Bell className="w-4 h-4" />
              })}
            >
              Primary Toast
            </Button>
            
            <Button
              color="secondary"
              variant="flat"
              startContent={<Activity className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Secondary Toast',
                description: 'Additional information available.',
                color: 'secondary',
                icon: <Activity className="w-4 h-4" />
              })}
            >
              Secondary Toast
            </Button>
            
            <Button
              color="success"
              variant="flat"
              startContent={<CheckCircle className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Success!',
                description: 'Operation completed successfully.',
                color: 'success',
                icon: <CheckCircle className="w-4 h-4" />
              })}
            >
              Success Toast
            </Button>
            
            <Button
              color="warning"
              variant="flat"
              startContent={<AlertTriangle className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Warning',
                description: 'Please review before proceeding.',
                color: 'warning',
                icon: <AlertTriangle className="w-4 h-4" />
              })}
            >
              Warning Toast
            </Button>
            
            <Button
              color="danger"
              variant="flat"
              startContent={<AlertCircle className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Error',
                description: 'Something went wrong. Please try again.',
                color: 'danger',
                icon: <AlertCircle className="w-4 h-4" />
              })}
            >
              Error Toast
            </Button>
          </div>
        </div>

        {/* Toast Variants */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Toast Variants</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              color="primary"
              variant="solid"
              onPress={() => addToast({
                title: 'Solid Variant',
                description: 'Toast with solid background.',
                color: 'primary',
                variant: 'solid',
                icon: <Star className="w-4 h-4" />
              })}
            >
              Solid Toast
            </Button>
            
            <Button
              color="primary"
              variant="bordered"
              onPress={() => addToast({
                title: 'Bordered Variant',
                description: 'Toast with border styling.',
                color: 'primary',
                variant: 'bordered',
                icon: <Heart className="w-4 h-4" />
              })}
            >
              Bordered Toast
            </Button>
            
            <Button
              color="primary"
              variant="flat"
              onPress={() => addToast({
                title: 'Flat Variant',
                description: 'Toast with flat styling.',
                color: 'primary',
                variant: 'flat',
                icon: <Zap className="w-4 h-4" />
              })}
            >
              Flat Toast
            </Button>
          </div>
        </div>

        {/* Toast Configurations */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Toast Configurations</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button
              color="secondary"
              variant="flat"
              onPress={() => addToast({
                title: 'No Icon Toast',
                description: 'Toast without an icon.',
                color: 'secondary',
                hideIcon: true
              })}
            >
              No Icon
            </Button>
            
            <Button
              color="success"
              variant="flat"
              onPress={() => addToast({
                title: 'Quick Toast',
                description: 'Disappears in 1 second.',
                color: 'success',
                timeout: 1000,
                icon: <Zap className="w-4 h-4" />
              })}
            >
              Quick Toast (1s)
            </Button>
            
            <Button
              color="warning"
              variant="flat"
              onPress={() => addToast({
                title: 'Persistent Toast',
                description: 'Stays for 10 seconds.',
                color: 'warning',
                timeout: 10000,
                icon: <Clock className="w-4 h-4" />
              })}
            >
              Long Toast (10s)
            </Button>
            
            <Button
              color="primary"
              variant="flat"
              onPress={() => addToast({
                title: 'Custom Styled',
                description: 'Toast with custom classes.',
                color: 'primary',
                icon: <Shield className="w-4 h-4" />,
                classNames: {
                  base: 'bg-gradient-to-r from-primary-500 to-secondary-500',
                  title: 'font-bold',
                  description: 'text-sm opacity-90'
                }
              })}
            >
              Custom Styled
            </Button>
          </div>
        </div>

        {/* Promise-based Toasts */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Promise-based Toasts</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              color="primary"
              variant="solid"
              onPress={() => addToast({
                title: 'Loading Operation',
                description: 'Processing your request...',
                color: 'primary',
                promise: simulatePromise(true),
                icon: <Activity className="w-4 h-4" />
              })}
            >
              Promise Success
            </Button>
            
            <Button
              color="danger"
              variant="solid"
              onPress={() => addToast({
                title: 'Loading Operation',
                description: 'Processing your request...',
                color: 'primary',
                promise: simulatePromise(false),
                icon: <Activity className="w-4 h-4" />
              })}
            >
              Promise Failure
            </Button>
            
            <Button
              color="secondary"
              variant="solid"
              onPress={() => addToast({
                title: 'Slow Operation',
                description: 'This will take 5 seconds...',
                color: 'secondary',
                promise: simulatePromise(true, 5000),
                icon: <Clock className="w-4 h-4" />
              })}
            >
              Slow Promise (5s)
            </Button>
          </div>
        </div>

        {/* Trading Journal Context */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Journal Toasts</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button
              color="success"
              variant="flat"
              startContent={<TrendingUp className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Trade Saved',
                description: 'Your winning trade has been recorded.',
                color: 'success',
                icon: <TrendingUp className="w-4 h-4" />
              })}
            >
              Trade Saved
            </Button>
            
            <Button
              color="danger"
              variant="flat"
              startContent={<TrendingDown className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Loss Recorded',
                description: 'Trade loss logged for analysis.',
                color: 'danger',
                icon: <TrendingDown className="w-4 h-4" />
              })}
            >
              Loss Recorded
            </Button>
            
            <Button
              color="primary"
              variant="flat"
              startContent={<DollarSign className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Balance Updated',
                description: 'Account balance has been synced.',
                color: 'primary',
                icon: <DollarSign className="w-4 h-4" />
              })}
            >
              Balance Update
            </Button>
            
            <Button
              color="warning"
              variant="flat"
              startContent={<Bell className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Risk Warning',
                description: 'Position size exceeds risk limits.',
                color: 'warning',
                icon: <Bell className="w-4 h-4" />
              })}
            >
              Risk Warning
            </Button>
          </div>
        </div>

        {/* System Notifications */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">System Notifications</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button
              color="success"
              variant="flat"
              startContent={<Wifi className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Connected',
                description: 'Successfully connected to trading platform.',
                color: 'success',
                icon: <Wifi className="w-4 h-4" />
              })}
            >
              Connection Success
            </Button>
            
            <Button
              color="danger"
              variant="flat"
              startContent={<WifiOff className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Connection Lost',
                description: 'Lost connection to trading platform.',
                color: 'danger',
                icon: <WifiOff className="w-4 h-4" />
              })}
            >
              Connection Lost
            </Button>
            
            <Button
              color="primary"
              variant="flat"
              startContent={<Activity className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Data Sync',
                description: 'Market data synchronized.',
                color: 'primary',
                icon: <Activity className="w-4 h-4" />
              })}
            >
              Data Sync
            </Button>
            
            <Button
              color="secondary"
              variant="flat"
              startContent={<Shield className="w-4 h-4" />}
              onPress={() => addToast({
                title: 'Backup Complete',
                description: 'Your data has been backed up.',
                color: 'secondary',
                icon: <Shield className="w-4 h-4" />
              })}
            >
              Backup Complete
            </Button>
          </div>
        </div>

        {/* Usage Guide */}
        <Card className="bg-surface/30">
          <CardHeader>
            <h3 className="text-lg font-semibold text-text-primary">Usage Guide</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <div>
                <Chip color="primary" variant="flat" size="sm" className="mb-2">Basic Usage</Chip>
                <pre className="text-xs bg-content1 p-3 rounded-md overflow-x-auto">
{`import { ToastProvider, useToastNotification } from '@enfomentis/ui';

function App() {
  return (
    <ToastProvider placement="top-right">
      <YourComponent />
    </ToastProvider>
  );
}

function YourComponent() {
  const { showToast } = useToastNotification();
  
  const handleClick = () => {
    showToast({
      title: 'Success!',
      description: 'Operation completed',
      color: 'success'
    });
  };
}`}
                </pre>
              </div>
              
              <div>
                <Chip color="secondary" variant="flat" size="sm" className="mb-2">Advanced Features</Chip>
                <ul className="text-sm text-text-secondary space-y-1 list-disc list-inside">
                  <li><strong>Colors:</strong> default, primary, secondary, success, warning, danger</li>
                  <li><strong>Variants:</strong> solid, bordered, flat</li>
                  <li><strong>Placement:</strong> top/bottom + left/center/right combinations</li>
                  <li><strong>Promise Support:</strong> Automatic loading states and result handling</li>
                  <li><strong>Custom Styling:</strong> Full classNames customization support</li>
                  <li><strong>Timeout Control:</strong> Configure auto-hide duration</li>
                </ul>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </PlaygroundPageLayout>
  );
};

/**
 * Main Toast Examples Component
 * Note: ToastProvider is configured globally in AppWrapper.tsx
 */
export const ToastExamples: React.FC = () => {
  return <ToastExamplesContent />;
};